import React from 'react'
import { Navigate } from 'react-router-dom'
import { useSession } from './useSession'
import { LOGIN_PATH } from '../lib/routes'
import styles from './RequireAuth.module.scss'

type RequireAuthProps = {
  children: React.ReactNode
}

/**
 * Route guard for the authenticated area.
 *
 *  - While the first `auth.me` is still in flight (`isLoading`), show a quiet
 *    loading state — never redirect. The silent refresh can add a round-trip,
 *    so bouncing to /login here would flash the login form on every reload of a
 *    valid session.
 *  - Once settled and unauthenticated (expired session, refresh failed), redirect
 *    to a clean /login rather than letting downstream queries surface errors.
 */
export const RequireAuth = ({ children }: RequireAuthProps): React.ReactElement => {
  const { isAuthenticated, isLoading } = useSession()

  if (isLoading) {
    return (
      <div className={`col-flx flx-a-c flx-j-c flx-gap-16 bg-light-cream ${styles['require-auth__loading']}`} role="status" aria-live="polite">
        <span className={styles['require-auth__spinner']} aria-hidden="true" />
        <span className={`txt-light-kale ${styles['require-auth__loading-text']}`}>Cargando tu sesión…</span>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={LOGIN_PATH} replace />
  }

  return <>{children}</>
}
